// THE KEYS A SPACE SEALS WITH, AND OPENS WITH.
//
// In your own space a schema's encrypted fields are sealed to YOUR public key
// (v2), or under the node's own data key (v3) once it is shared. In a group they
// are sealed to the GROUP's public key, so every member holding the group key can
// open them. Sealing to the writer's own key there is how a member wrote a field
// nobody else in the group could ever read.
//
//   group public key   the group account's (GET /groups/<g>/enc-privkey answers it
//                      with your wrapped copy; else /users/<group_user_id>/enc-public-key).
//                      Sealing needs no unlock: it is a public key.
//   group private keys every generation you hold (GET /groups/<g>/key-generations),
//                      each unwrapped with your own private key; newest first, so a
//                      field sealed before a re-key still opens with the key of its day.
//
// An edit keeps the node as it was sealed: a field you did not change keeps its
// ciphertext byte for byte; a field you changed is sealed the way the node is
// (its v3 data key when it has one, else the space's key). The fields sealed are
// the node's own schema version's, the one the server validates the edit against.
import api from '../services/api';
import {
    importPublicKey, unwrapPrivateKeyFromApp, decryptValueJsonAuto, encryptFieldV2, encryptFieldV3,
    isEncrypted, findEncryptedFields, unwrapDek,
} from './fieldEncryption';

const V3 = 'paradigm_enc:v3:';

function bytesOf(b64url) {
    const b64 = String(b64url).replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}

// gid → Promise<CryptoKey|null> (public); per unlocked key, gid → Promise<CryptoKey[]> (private)
const publicCache = new Map();
const privateCache = new WeakMap(); // userPrivateKey → Map(gid → Promise<CryptoKey[]>)
const privateMaps = new Set();

/** Forget what was read of a group's keys (after a re-key, a sign-out, tests). */
export function forgetGroupKeys(gid) {
    if (gid) publicCache.delete(String(gid));
    else publicCache.clear();
    // the private side is keyed by your unlocked key; a re-key adds a generation, so
    // it is read again too
    privateMaps.forEach((m) => (gid ? m.delete(String(gid)) : m.clear()));
}

/** The group's current public key, or null when the group has none (not encrypted). */
export function groupPublicKey(gid) {
    const key = String(gid);
    if (publicCache.has(key)) return publicCache.get(key);
    const p = (async () => {
        let b64 = null;
        try { b64 = (await api.request(`/groups/${encodeURIComponent(gid)}/enc-privkey`))?.enc_public_key || null; } catch { /* not keyed yet */ }
        if (!b64) {
            try {
                const g = await api.getGroup(gid);
                const grp = g?.group || g;
                if (grp?.encryption_enabled !== false && grp?.group_user_id) {
                    b64 = (await api.request(`/users/${encodeURIComponent(grp.group_user_id)}/enc-public-key`))?.enc_public_key || null;
                }
            } catch { /* no key */ }
        }
        return b64 ? importPublicKey(bytesOf(b64)) : null;
    })().catch(() => null).then((k) => { if (!k) publicCache.delete(key); return k; });
    publicCache.set(key, p);
    return p;
}

/** Every generation of the group key you hold, opened with your key: newest first ([] when none). */
export function groupPrivateKeys(gid, userPrivateKey) {
    if (!userPrivateKey) return Promise.resolve([]);
    let m = privateCache.get(userPrivateKey);
    if (!m) { m = new Map(); privateCache.set(userPrivateKey, m); privateMaps.add(m); }
    const key = String(gid);
    if (m.has(key)) return m.get(key);
    const p = (async () => {
        let wrapped = [];
        try {
            const r = await api.request(`/groups/${encodeURIComponent(gid)}/key-generations`);
            wrapped = (r?.generations || []).slice().sort((a, b) => (b.generation || 0) - (a.generation || 0)).map((g) => g.enc_group_privkey).filter(Boolean);
        } catch { /* an older server: the current key alone */ }
        if (!wrapped.length) {
            try {
                const r = await api.request(`/groups/${encodeURIComponent(gid)}/enc-privkey`);
                if (r?.enc_group_privkey) wrapped = [r.enc_group_privkey];
            } catch { /* not keyed */ }
        }
        const keys = [];
        for (const w of wrapped) {
            try { keys.push(await unwrapPrivateKeyFromApp(w, userPrivateKey)); } catch { /* not wrapped to you */ }
        }
        return keys;
    })().then((keys) => { if (!keys.length) m.delete(key); return keys; });
    m.set(key, p);
    return p;
}

const stillSealed = (v) => findEncryptedFields(v).length > 0;

/**
 * A node's fields, opened where the keys you hold reach. In a group: every
 * generation of the group key, then your own (a field a member sealed to
 * themselves before groups sealed to the group). In your own space: your key,
 * and the node's v3 data key. Never throws; what stays shut stays ciphertext.
 */
export async function openFields(node, { group = null, encKeys = null, dek = null } = {}) {
    const v = node?.value_json || {};
    if (!stillSealed(v) || !encKeys?.privateKey) return v;
    let out = v;
    if (group) {
        const keys = await groupPrivateKeys(group, encKeys.privateKey).catch(() => []);
        for (const k of keys) {
            if (!stillSealed(out)) break;
            out = await decryptValueJsonAuto(out, { privateKey: k });
        }
    } else if (!dek && Object.values(v).some((x) => typeof x === 'string' && x.startsWith(V3))) {
        dek = await nodeDek(node.id, encKeys.privateKey);
    }
    if (stillSealed(out)) out = await decryptValueJsonAuto(out, encKeys, dek);
    return out;
}

/** A personal node's v3 data key, opened with your key; null when there is none. */
export async function nodeDek(nodeId, privateKey) {
    if (!nodeId || !privateKey) return null;
    try {
        const k = await api.request(`/nodes/${nodeId}/key`);
        return k?.wrapped_dek ? await unwrapDek(k.wrapped_dek, privateKey) : null;
    } catch { return null; }
}

/** The words for a group that has no key to seal with. */
export const noGroupKeyLine = (groupName) => `${groupName || 'This group'} has no encryption key yet, so its encrypted fields can’t be written. An admin can turn on encryption in Settings, This group.`;

const same = (a, b) => {
    try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
};

/**
 * Seal a schema's encrypted fields for the space you write in.
 *
 *   fields   the encrypted fields of the schema version the write is checked
 *            against (a new node: the schema's; an edit: the node's own version)
 *   node     on an edit, the node as stored: a field whose opened value you did
 *            not change keeps its stored ciphertext
 *   opened   on an edit, the values as they were opened for you
 *
 * In a group, sealed to the group key; a group with no key refuses (throws),
 * never falling back to your own key. In your own space, a node sealed under a
 * v3 data key stays v3 under that key; otherwise your public key (v2).
 */
export async function sealFields(value, fields, { group = null, groupName = null, encKeys = null, node = null, opened = null } = {}) {
    const list = (fields || []).filter((f) => value && f in value && value[f] != null);
    if (!list.length) return value;
    const out = { ...value };
    const stored = node?.value_json || {};
    const toSeal = [];
    for (const f of list) {
        if (isEncrypted(out[f])) continue;                                  // already ciphertext
        if (opened && f in opened && isEncrypted(stored[f]) && same(out[f], opened[f])) { out[f] = stored[f]; continue; }
        toSeal.push(f);
    }
    if (!toSeal.length) return out;
    if (group) {
        const pub = await groupPublicKey(group);
        if (!pub) throw new Error(noGroupKeyLine(groupName));
        for (const f of toSeal) out[f] = await encryptFieldV2(out[f], f, pub);
        return out;
    }
    // your own space: the node's v3 data key when it is sealed that way
    const isV3 = Object.values(stored).some((x) => typeof x === 'string' && x.startsWith(V3));
    if (isV3 && node?.id) {
        const dek = await nodeDek(node.id, encKeys?.privateKey);
        if (dek) {
            for (const f of toSeal) out[f] = await encryptFieldV3(out[f], f, dek);
            return out;
        }
    }
    if (!encKeys?.publicKey) return value;                                   // no key: the server says why
    for (const f of toSeal) out[f] = await encryptFieldV2(out[f], f, encKeys.publicKey);
    return out;
}
